import { useState } from 'react';
import { format } from 'date-fns';
import { useDateNow, useReduxState } from 'hooks';
import { getMessageWithPlaceholders } from 'shared-locales';
import { Alert, Button, ConfirmButton } from 'components/common';
import {
  ReaderCompletionFormRequirement,
  ReaderCompletionFormSignature
} from '.';

import type {
  ReaderCourseData,
  ReaderSettingsFont,
  ReaderSettingsFontSize
} from 'shared-types';
import type { ButtonSize } from 'components/common/Button';

interface Props {
  courseData: Partial<
    Pick<
      ReaderCourseData,
      | 'futureChapterLatestPublishDate'
      | 'isAddedOrGroupOrPathUser'
      | 'isCourseAdmin'
      | 'isCourseOwner'
      | 'isSuperAdmin'
    >
  > & {
    course: Pick<
      ReaderCourseData['course'],
      | 'completionAllowRetries'
      | 'completionElectronicSignature'
      | 'completionFormCustomInstructions'
      | 'completionReading'
      | 'completionTasks'
      | 'completionVideos'
      | 'enableAdminAnalytics'
    >;
    courseRequirements?: Pick<
      ReaderCourseData['courseRequirements'],
      'numberOfTasks' | 'numberOfVideos' | 'taskMaxPoints'
    >;
    toc?: Pick<ReaderCourseData['toc'], 'chapters' | 'chaptersOrder'>;
  };
  error?: string;
  font?: ReaderSettingsFont;
  fontSize?: ReaderSettingsFontSize;
  onSubmit: (values: any) => Promise<void>;
  submitting?: boolean;
}

const ReaderCompletionFormContent = ({
  courseData,
  error,
  font,
  fontSize,
  onSubmit,
  submitting
}: Props) => {
  const { dateNow } = useDateNow();

  const {
    messages,
    pageState: { completion, offline, progressData },
    serverConfig: {
      courseCompletionElectronicSignature,
      courseCompletionHideRequirements,
      courseCompletionHideResults
    },
    theme,
    user
  } = useReduxState();

  const [signaturePassword, setSignaturePassword] = useState('');
  const [signaturePasswordError, setSignaturePasswordError] = useState<
    string | undefined
  >(undefined);

  if (!courseData) return null;

  const requireSignature =
    courseCompletionElectronicSignature?.isEnabled &&
    courseData.course.completionElectronicSignature;

  const handleSubmit = async () => {
    if (
      requireSignature &&
      ['local', 'local-totp'].includes(user?.authenticatorType) &&
      !signaturePassword
    ) {
      setSignaturePasswordError(messages.commonFieldRequired);
    }
    if (onSubmit) await onSubmit({ password: signaturePassword });
    return true;
  };

  const courseHasRequirements =
    (courseData.course?.completionTasks &&
      courseData.courseRequirements?.numberOfTasks) ||
    courseData.course?.completionReading ||
    courseData.course?.completionVideos;

  const chaptersWithMissingRequiredAnswers: {
    missingRequiredAnswers: number;
    number: number;
    title: string;
  }[] = [];

  const isMissingRequiredAnswers =
    progressData?.numberOfRequiredAnswers <
      progressData?.numberOfRequiredTasks || false;

  const isCompletable =
    (!courseData?.futureChapterLatestPublishDate &&
      !isMissingRequiredAnswers) ||
    false;

  if (isMissingRequiredAnswers) {
    (courseData.toc?.chaptersOrder || []).forEach((chapterId) => {
      const chapter = courseData.toc.chapters[chapterId];
      if (!chapter.publishDate || Date.parse(chapter.publishDate) > dateNow) {
        return;
      }

      const chapterProgress = progressData?.chapters[chapterId];
      if (chapterProgress?.missingRequiredAnswers) {
        chaptersWithMissingRequiredAnswers.push({
          missingRequiredAnswers: chapterProgress.missingRequiredAnswers,
          number: chapter.number,
          title: chapter.title
        });
      }
    });
  }

  const defaultButtonText = requireSignature
    ? messages.readerCompletionSignAndCheck
    : messages.readerCompletionCheck;

  const languageVersion = completion?.courseLanguageVersion;
  const customInstructions =
    courseData.course.completionFormCustomInstructions || {};

  const getLocalizedCustomContent = (
    main: string | undefined,
    locales: Record<string, string> | undefined
  ) => {
    if (languageVersion && locales && locales[languageVersion]) {
      return locales[languageVersion];
    }
    // use default content if no language version specific content exists
    return main;
  };

  const useCustomInstructions = customInstructions.isEnabled;

  const completionFormContent = useCustomInstructions
    ? {
        content:
          getLocalizedCustomContent(
            customInstructions.content,
            customInstructions.contentLocales
          ) || `<p>${messages.readerCompletionInfo}</p>`,
        buttonText:
          getLocalizedCustomContent(
            customInstructions.buttonText,
            customInstructions.buttonTextLocales
          ) || defaultButtonText,
        showRequirements:
          (!courseCompletionHideRequirements &&
            customInstructions.showRequirements) ||
          false,
        title:
          getLocalizedCustomContent(
            customInstructions.title,
            customInstructions.titleLocales
          ) || messages.readerCompletionHeading
      }
    : {
        content: `<p>${messages.readerCompletionInfo}</p>`,
        buttonText: defaultButtonText,
        showRequirements: !courseCompletionHideRequirements,
        title: messages.readerCompletionHeading
      };

  const futureChapterMessage = courseData?.futureChapterLatestPublishDate
    ? getMessageWithPlaceholders({
        message: messages.readerCompletionBlockedByFutureChapter,
        placeholders: {
          futureDate: format(
            courseData.futureChapterLatestPublishDate,
            'dd.M.yyyy HH:MM'
          )
        }
      })
    : '';

  return (
    <div
      className="content"
      style={{
        fontSize: {
          small: '1.4rem',
          medium: '1.5rem',
          large: '1.6rem',
          huge: '2rem'
        }[fontSize]
      }}
    >
      <div className="inner">
        <div className="title" style={{ fontFamily: font }}>
          {completionFormContent.title}
        </div>

        <div className="description" style={{ fontFamily: font }}>
          <div
            dangerouslySetInnerHTML={{
              __html: completionFormContent.content
            }}
          />

          {!courseCompletionHideRequirements && !useCustomInstructions
            ? courseHasRequirements
              ? ` ${messages.readerCompletionInfoRequirements}`
              : ` ${messages.readerCompletionInfoNoRequirements}`
            : ''}
        </div>

        {!courseCompletionHideRequirements &&
        !courseCompletionHideResults &&
        completionFormContent.showRequirements ? (
          <div className="requirements" style={{ fontFamily: font }}>
            {courseData.course.completionTasks &&
            courseData.courseRequirements?.numberOfTasks &&
            courseData.courseRequirements?.taskMaxPoints > 0 ? (
              <ReaderCompletionFormRequirement
                fontSize={fontSize}
                icon="check"
                label={messages.tasksTaskPoints}
                value={courseData.course.completionTasks}
              />
            ) : null}

            {courseData.course.completionReading ? (
              <ReaderCompletionFormRequirement
                fontSize={fontSize}
                icon="glasses"
                label={messages.tasksReading}
                value={courseData.course.completionReading}
              />
            ) : null}

            {courseData.course.completionVideos &&
            courseData.courseRequirements?.numberOfVideos ? (
              <ReaderCompletionFormRequirement
                fontSize={fontSize}
                icon="play"
                label={messages.tasksVideos}
                value={courseData.course.completionVideos}
              />
            ) : null}
          </div>
        ) : null}

        {courseHasRequirements && !useCustomInstructions ? (
          <div className="info" style={{ fontFamily: font }}>
            {courseData.course.completionAllowRetries
              ? messages.readerCompletionInfoRetry
              : messages.readerCompletionInfoNoRetries}
          </div>
        ) : null}

        {requireSignature ? (
          <ReaderCompletionFormSignature
            onPasswordChange={setSignaturePassword}
            onPasswordErrorChange={setSignaturePasswordError}
            passwordError={signaturePasswordError}
          />
        ) : null}

        {(courseData.isCourseOwner || courseData.isSuperAdmin) &&
        courseData.toc?.chaptersOrder?.some(
          (chapterId) => !courseData.toc.chapters[chapterId].publishDate
        ) ? (
          <div className="warning">
            <Alert
              icon="exclamation-triangle"
              size={fontSize}
              text={messages.readerCompletionUnpublishedWarning}
              type="warning-light"
            />
          </div>
        ) : null}

        {!courseData.course.enableAdminAnalytics &&
        !courseData.isAddedOrGroupOrPathUser &&
        (courseData.isCourseOwner ||
          courseData.isCourseAdmin ||
          courseData.isSuperAdmin) ? (
          <div className="warning">
            <Alert
              icon="exclamation-triangle"
              size={fontSize}
              text={messages.readerCompletionAdminInfo}
              type="warning-light"
            />
          </div>
        ) : null}

        {progressData && chaptersWithMissingRequiredAnswers.length ? (
          <Alert
            expand={true}
            size="medium"
            style={{ width: '100%' }}
            text={
              <div className="required-chapters">
                {chaptersWithMissingRequiredAnswers.map(
                  (chapterData, index) => (
                    <div className="required-chapter" key={index}>
                      <div className="required-chapter-number">
                        {chapterData.number}
                      </div>
                      <div className="required-chapter-title">
                        {`${chapterData.title}:`}
                      </div>
                      <div className="required-chapter-answers">
                        {getMessageWithPlaceholders({
                          message: messages.commonNumberOfAnswersMissing,
                          placeholders: {
                            numberOfAnswers: chapterData.missingRequiredAnswers
                          }
                        })}
                      </div>
                    </div>
                  )
                )}
              </div>
            }
            title={messages.readerCompletionInfoMissingRequiredAnswers}
            type="warning-light"
          />
        ) : null}

        {progressData?.numberOfTasks &&
        progressData.taskAnswers < progressData.numberOfTasks ? (
          <div className="complete-button">
            <ConfirmButton
              actionText={messages.commonSubmit}
              buttonSize={fontSize}
              buttonText={completionFormContent.buttonText}
              category="success"
              confirmText={
                <div>
                  {messages.readerCompletionConfirmSubmit}

                  {courseData.toc?.chaptersOrder
                    ?.filter(
                      (chapterId) =>
                        progressData?.chapters?.[chapterId]?.missingAnswers
                    )
                    .map((chapterId) => (
                      <div className="chapter" key={chapterId}>
                        {`${courseData.toc?.chapters?.[chapterId].number}. `}
                        {courseData.toc?.chapters?.[chapterId].title}
                        <div className="chapter-number">
                          {progressData?.chapters?.[chapterId]?.missingAnswers}
                        </div>
                      </div>
                    ))}
                </div>
              }
              confirmTitle={messages.readerCompletionHeading}
              disabled={offline || !isCompletable}
              onConfirm={handleSubmit}
              {...(futureChapterMessage
                ? { buttonTooltip: futureChapterMessage }
                : {})}
            />
          </div>
        ) : (
          <div className="complete-button">
            <Button
              category="success"
              disabled={offline || !isCompletable}
              loading={submitting}
              onClick={handleSubmit}
              size={fontSize as ButtonSize}
              text={completionFormContent.buttonText}
              {...(futureChapterMessage
                ? { tooltip: futureChapterMessage }
                : {})}
            />
          </div>
        )}

        {error ? (
          <div className="error">
            <Alert text={error} type="error-light" />
          </div>
        ) : null}
      </div>

      <style jsx>{`
        .content {
          color: ${theme.textColorLighter};
          font-weight: 600;
        }
        .title {
          color: ${theme.textColor};
          font-size: 1.3em;
          font-weight: 700;
          margin: 0 0 15px;
        }
        .requirements {
          margin: 15px 0;
        }
        .info {
          margin: 15px 0;
        }
        .warning {
          margin: 15px 0;
          text-align: left;
        }
        .error {
          margin-top: 15px;
        }
        .chapter {
          margin-top: 8px;
        }
        .chapter:first-of-type {
          margin-top: 15px;
        }
        .chapter-number {
          background-color: ${theme.dangerLightColor};
          border-radius: 10px;
          color: ${theme.dangerLightTextColor};
          display: inline-block;
          font-size: 1.1rem;
          font-weight: 700;
          line-height: 1;
          margin-left: 7px;
          padding: 3px 6px;
          position: relative;
          top: -1px;
        }
        .complete-button {
          margin-top: 15px;
        }
        .required-chapters {
          display: flex;
          flex-direction: column;
          gap: 7px;
        }
        .required-chapter {
          align-items: center;
          display: flex;
          font-size: 1.4rem;
          gap: 7px;
        }
        .required-chapter-number {
          align-items: center;
          background-color: ${theme.warningLightTextColor};
          border-radius: 10px;
          color: ${theme.warningLightColor};
          display: flex;
          font-size: 1.1rem;
          font-weight: 700;
          justify-content: center;
          width: 20px;
          height: 20px;
        }
      `}</style>
    </div>
  );
};

export default ReaderCompletionFormContent;
